import type * as Types from 'app/types/graphql';

import { CampaignWithVariantTemplateSelectFragmentDoc } from '../fragments/campaignTemplate.generated';
import {
  CampaignSelectFragmentDoc,
  CampaignVersionSelectFragmentDoc,
} from '../fragments/campaign.generated';
import {
  VariantWithTemplateSelectFragmentDoc,
  VariantSelectFragmentDoc,
} from '../fragments/variant.generated';
import { VariantTemplateSelectFragmentDoc } from '../fragments/variantTemplate.generated';
import { VariantPriceSelectFragmentDoc } from '../fragments/variantPrice.generated';
import { VariantProductVariantSelectFragmentDoc } from '../fragments/variantProductVariant.generated';
import { VariantUrlSelectFragmentDoc } from '../fragments/variantUrl.generated';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { appFetcher } from '~/api/utils/gatewayClient';
export type CampaignTemplateQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;

export type CampaignTemplateQueryResponse = {
  campaign?: Types.Maybe<
    Pick<
      Types.Campaign,
      | 'id'
      | 'name'
      | 'type'
      | 'status'
      | 'priority'
      | 'storeDomain'
      | 'targetDevices'
      | 'targetChannels'
      | 'targetCountries'
      | 'targetVisitorTypes'
      | 'conditions'
      | 'createdAt'
      | 'updatedAt'
      | 'startedAt'
      | 'deletedAt'
      | 'completedAt'
    > & {
      variants?: Types.Maybe<
        Array<
          Types.Maybe<
            Pick<
              Types.Variant,
              | 'id'
              | 'isOrigin'
              | 'isWinner'
              | 'trafficRate'
              | 'themePageID'
              | 'templateType'
              | 'templateSuffix'
              | 'createdAt'
              | 'deletedAt'
              | 'updatedAt'
              | 'winnerTemplateSuffix'
            > & {
              template?: Types.Maybe<
                Pick<
                  Types.VariantTemplate,
                  | 'createdAt'
                  | 'deletedAt'
                  | 'id'
                  | 'redirectURL'
                  | 'themeID'
                  | 'updatedAt'
                  | 'thumbnailURL'
                  | 'title'
                >
              >;
              price?: Types.Maybe<
                Pick<
                  Types.VariantPrice,
                  | 'id'
                  | 'baseID'
                  | 'handle'
                  | 'status'
                  | 'title'
                  | 'redirectURL'
                  | 'sourceProductBaseID'
                >
              >;
              productVariants?: Types.Maybe<
                Array<
                  Types.Maybe<
                    Pick<
                      Types.VariantProductVariant,
                      'id' | 'baseID' | 'name' | 'price' | 'compareAtPrice' | 'variantID'
                    >
                  >
                >
              >;
              url?: Types.Maybe<
                Pick<
                  Types.VariantUrl,
                  'id' | 'baseID' | 'handle' | 'title' | 'redirectURL' | 'resourceType'
                >
              >;
            }
          >
        >
      >;
      group?: Types.Maybe<Pick<Types.CampaignGroup, 'id' | 'title' | 'type'>>;
      versions?: Types.Maybe<
        Array<
          Types.Maybe<
            Pick<
              Types.SplitTestingCampaignVersion,
              'id' | 'version' | 'settings' | 'startedAt' | 'completedAt'
            >
          >
        >
      >;
    }
  >;
};

export const CampaignTemplateDocument = `
    query CampaignTemplate($id: ID!) {
  campaign(id: $id) {
    ...CampaignWithVariantTemplateSelect
  }
}
    ${CampaignWithVariantTemplateSelectFragmentDoc}
${CampaignSelectFragmentDoc}
${CampaignVersionSelectFragmentDoc}
${VariantWithTemplateSelectFragmentDoc}
${VariantSelectFragmentDoc}
${VariantTemplateSelectFragmentDoc}
${VariantPriceSelectFragmentDoc}
${VariantProductVariantSelectFragmentDoc}
${VariantUrlSelectFragmentDoc}`;

export const useCampaignTemplateQuery = <TData = CampaignTemplateQueryResponse, TError = unknown>(
  variables: CampaignTemplateQueryVariables,
  options?: UseQueryOptions<CampaignTemplateQueryResponse, TError, TData>,
) => {
  return useQuery<CampaignTemplateQueryResponse, TError, TData>(
    ['CampaignTemplate', variables],
    appFetcher<CampaignTemplateQueryResponse, CampaignTemplateQueryVariables>(
      CampaignTemplateDocument,
      variables,
    ),
    options,
  );
};

useCampaignTemplateQuery.getKey = (variables: CampaignTemplateQueryVariables) => [
  'CampaignTemplate',
  variables,
];
